import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShopContext } from '../../context/ShopContext';

const SLIDE_INTERVAL = 600*4; // 🔥 faster
const ANIMATION_DURATION = 300*4;

const HeroSlider: React.FC = () => {
  const { stores, loading: shopLoading, fetchStores } = useShopContext();
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 🔁 Load stores once
  useEffect(() => {
    if (!stores.length) fetchStores();
  }, []);

  // ⚡ Auto slide (stable interval)
  useEffect(() => {
    if (!stores.length) return;

    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % stores.length);
    }, SLIDE_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [stores.length]);

  // 🖼️ Preload images for instant switch
  useEffect(() => {
    stores.forEach((store) => {
      const img = new Image();
      img.src =
        store.theme?.background_image_desktop ||
        store.theme?.logo ||
        '/default-store-banner.jpg';
    });
  }, [stores]);

  const currentStore = useMemo(() => stores[currentSlide], [stores, currentSlide]);

  if (shopLoading) {
    return (
      <section className="h-96 flex items-center justify-center bg-gray-200">
        <p className="text-gray-600">Loading stores...</p>
      </section>
    );
  }

  if (!currentStore) return null;

  return (
    <section
      id="hero-slider"
      className="relative h-96 flex items-center justify-center transition-opacity"
      style={{
        transitionDuration: `${ANIMATION_DURATION}ms`,
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${
          currentStore.theme?.background_image_desktop ||
          currentStore.theme?.logo ||
          '/default-store-banner.jpg'
        })`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="text-center text-white px-4 animate-fadeIn">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          {currentStore.name}
        </h1>
        <p className="text-xl mb-6">Discover amazing products</p>
        <button
          className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-6 rounded-full text-lg"
          onClick={() => navigate(`/products?store=${currentStore.id}`)}
        >
          Shop Now
        </button>
      </div>

      {/* Indicators */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
        {stores.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-transform ${
              index === currentSlide
                ? 'bg-white scale-110'
                : 'bg-gray-400'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
